import { useMemo } from 'react'

import { TokenChipField } from '@/components/authoring'
import { useSkills } from '@/hooks/use-skills'
import {
  buildRelatedSkillSuggestions,
  resolveSkillNameFromSuggestions,
} from '@/utils/profile-skill-suggestions'

/**
 * Single-field skill tagger for plain skill-name lists (experience, education,
 * certifications): chips render inside the field, typing filters catalog +
 * profile suggestions, and unmatched names get an explicit "Create" row.
 * Skills with per-item metadata (job required toggle, profile evidence) keep
 * the SkillManager row paradigm instead — see skill-manager-parts.tsx.
 *
 * Composes shared {@link TokenChipField} with the skills catalog.
 */
export function SkillChipField({
  id,
  skills,
  onAdd,
  onRemove,
  profileSkills,
  disabled,
  placeholder = 'Search or type a skill…',
  helperText = 'Pick a match or create a new skill — press Backspace to remove the last one.',
}: {
  id: string
  skills: string[]
  onAdd: (skillName: string) => void
  onRemove: (skillName: string) => void
  profileSkills?: string[]
  disabled?: boolean
  placeholder?: string
  helperText?: string
}) {
  const { skills: catalogSkills, isLoading, error } = useSkills()

  const pool = useMemo(
    () => buildRelatedSkillSuggestions(catalogSkills, profileSkills),
    [catalogSkills, profileSkills],
  )

  const selectedLower = useMemo(
    () => new Set(skills.map((s) => s.trim().toLowerCase()).filter(Boolean)),
    [skills],
  )

  const suggestions = useMemo(
    () => pool.filter((item) => !selectedLower.has(item.name.trim().toLowerCase())),
    [pool, selectedLower],
  )

  return (
    <div className="space-y-1.5">
      <TokenChipField
        id={id}
        tokens={skills}
        onAdd={(raw) => {
          const q = raw.trim().toLowerCase()
          const filtered = suggestions.filter((item) => {
            const name = item.name.trim().toLowerCase()
            return name === q || name.includes(q)
          })
          onAdd(resolveSkillNameFromSuggestions(raw, filtered.length ? filtered : suggestions))
        }}
        onRemove={onRemove}
        suggestions={suggestions}
        suggestionsLoading={isLoading}
        disabled={disabled}
        placeholder={placeholder}
        helperText={helperText}
        emptySuggestionsLabel="Start typing to search skills, or type a new skill name."
        createLabel={(q) => `Create "${q}"`}
      />
      {error ? (
        <p className="text-xs text-destructive" role="status">
          Could not load skill suggestions (
          {error instanceof Error ? error.message : String(error)}). You can still type skill
          names manually.
        </p>
      ) : null}
    </div>
  )
}
